import React, { useState, useEffect } from 'react';
import {
    collection, addDoc, getDocs, updateDoc,
    deleteDoc, doc, query, where, getDoc, serverTimestamp
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

// MUI Components
import {
    Paper, Typography, TextField, Button, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, InputAdornment, Pagination, CircularProgress, Divider,
    MenuItem, Select, FormControl, InputLabel, Dialog, DialogTitle,
    DialogContent, DialogActions, Tooltip, Grid, Chip,
    Stack
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    Add as AddIcon,
    Close as CloseIcon,
    Inventory as StockIcon
} from '@mui/icons-material';

const StoreProducts = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    
    // Form State
    const [open, setOpen] = useState(false);
    const [productName, setProductName] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [sellPrice, setSellPrice] = useState('');
    const [schoolId, setSchoolId] = useState(null);
    const [editId, setEditId] = useState(null);

    // UI State
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const userDoc = await getDoc(doc(db, "users", user.uid));
                if (userDoc.exists()) {
                    const sId = userDoc.data().schoolId;
                    setSchoolId(sId);
                    fetchInitialData(sId);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const fetchInitialData = async (sId) => {
        setLoading(true);
        try {
            // 1. Fetch Categories
            const catSnap = await getDocs(query(collection(db, "categories"), where("schoolId", "==", sId)));
            const catList = catSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCategories(catList);

            // 2. Fetch Purchase Orders (Inward)
            const purSnap = await getDocs(query(collection(db, "purchaseOrders"), where("schoolId", "==", sId)));
            const purchaseMap = {};
            purSnap.docs.forEach(doc => {
                const items = doc.data().items || [];
                items.forEach(item => {
                    purchaseMap[item.productId] = (purchaseMap[item.productId] || 0) + parseInt(item.qty || 0);
                });
            });

            // 3. Fetch Student Inventory (Outward/Issued)
            const issueSnap = await getDocs(query(collection(db, "studentInventory"), where("schoolId", "==", sId)));
            const issueMap = {};
            issueSnap.docs.forEach(doc => {
                const items = doc.data().items || [];
                items.forEach(item => {
                    issueMap[item.productId] = (issueMap[item.productId] || 0) + parseInt(item.qty || 0);
                });
            });

            // 4. Fetch Products and Combine
            const prodSnap = await getDocs(query(collection(db, "products"), where("schoolId", "==", sId)));
            const prodList = prodSnap.docs.map(doc => {
                const data = doc.data();
                const purchased = purchaseMap[doc.id] || 0;
                const issued = issueMap[doc.id] || 0;
                return {
                    id: doc.id,
                    ...data,
                    purchased,
                    issued,
                    stock: purchased - issued
                };
            });

            setProducts(prodList);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
        setLoading(false);
    };

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setEditId(null);
        setProductName('');
        setSelectedCategory('');
        setSellPrice('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!productName.trim() || !selectedCategory) return;

        try {
            const productData = {
                productName,
                sellPrice: parseFloat(sellPrice || 0),
                categoryId: selectedCategory,
                categoryName: categories.find(c => c.id === selectedCategory)?.catName || '',
                schoolId,
                updatedAt: serverTimestamp()
            };

            if (editId) {
                await updateDoc(doc(db, "products", editId), productData);
            } else {
                await addDoc(collection(db, "products"), {
                    ...productData,
                    createdAt: serverTimestamp()
                });
            }
            handleClose();
            fetchInitialData(schoolId);
        } catch (error) {
            console.error("Error saving product:", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Delete this product? Stock history will be affected.")) {
            await deleteDoc(doc(db, "products", id));
            fetchInitialData(schoolId);
        }
    };

    const startEdit = (prod) => {
        setEditId(prod.id);
        setProductName(prod.productName);
        setSelectedCategory(prod.categoryId);
        setSellPrice(prod.sellPrice || '');
        setOpen(true);
    };

    const filteredData = products.filter(item =>
        item.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.categoryName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const paginatedItems = filteredData.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress size={30} /></Box>;

    return (
        <Grid sx={{ mt: 1, mb: 4 }}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="700" color="primary">
                            Product Master & Inventory
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Live Stock = Total Purchased - Total Issued to Students
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        onClick={handleOpen}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        New Product
                    </Button>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Search Bar */}
                <Box sx={{ mb: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Search products or categories..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 350 }}
                    />
                </Box>

                {/* Inventory Table */}
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f7f9' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Product Details</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Purchased</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Issued</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Current Stock</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Sell Price</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedItems.map((prod, index) => (
                                <TableRow key={prod.id} hover>
                                    <TableCell>{(page - 1) * itemsPerPage + index + 1}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="600">{prod.productName}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={prod.categoryName} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell align="center">{prod.purchased}</TableCell>
                                    <TableCell align="center">{prod.issued}</TableCell>
                                    <TableCell align="center">
                                        <Chip 
                                            label={prod.stock} 
                                            size="small" 
                                            sx={{ 
                                                fontWeight: 'bold',
                                                bgcolor: prod.stock <= 0 ? '#ffebee' : '#e8f5e9',
                                                color: prod.stock <= 0 ? '#c62828' : '#2e7d32',
                                                minWidth: 45
                                            }} 
                                        />
                                    </TableCell>
                                    <TableCell align="right">₹{prod.sellPrice || 0}</TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" justifyContent="flex-end" spacing={1}>
                                            <IconButton size="small" color="primary" onClick={() => startEdit(prod)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(prod.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={Math.ceil(filteredData.length / itemsPerPage)}
                        page={page}
                        onChange={(e, v) => setPage(v)}
                        color="secondary"
                        size="small"
                    />
                </Box>
            </Paper>

            {/* Product Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">{editId ? 'Update Product' : 'Add New Product'}</Typography>
                    <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <Grid className="row" spacing={2}>
                            <Grid className="col-sm-6 mb-3" item xs={12}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Category</InputLabel>
                                    <Select
                                        required
                                        value={selectedCategory}
                                        label="Category"
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                    >
                                        {categories.map((cat) => (
                                            <MenuItem key={cat.id} value={cat.id}>{cat.catName}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid className="col-sm-6 mb-3" item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Product Name"
                                    size="small"
                                    value={productName}
                                    onChange={(e) => setProductName(e.target.value)}
                                />
                            </Grid>
                            <Grid className="col-sm-6 mb-3" item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Selling Price (₹)"
                                    type="number"
                                    size="small"
                                    value={sellPrice}
                                    onChange={(e) => setSellPrice(e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleClose} color="inherit">Cancel</Button>
                        <Button type="submit" variant="contained" color="secondary">
                            {editId ? 'Update' : 'Add Product'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Grid>
    );
};

export default StoreProducts;